// Shared logic for the admin / company-information "Edit User" pages.
// The two pages render slightly different JSX (company-information also lets
// the operator edit name/surname/email/phone) but the state machine driving
// role, product, claim and address selection is identical. Keeping it here
// avoids duplicating ~500 lines of logic between the two page components.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Text, useText, successDialog, errorDialog } from '@src/components';
import { warningDialog } from '@src/components/CustomDialog';
import {
    getListUserAddressLookup,
    getUserCheckedFunctions,
    getUserInfo,
    updateUserAndRole,
    userContactLookup,
    setTempFormList,
    setClaimList,
    setNewClaimList,
    setNewAddressList,
    getCheckList,
    setDeletedAddress,
    setDeletedClaims,
    setAddressSelectionType,
} from '@src/store/slice/userSlice';
import { getActiveProducts } from '@src/store/slice/customerVouGroupsSlice';
import { getUserTypesList } from '@src/store/slice/userTypesSlice';
import { useLanguageChange } from '@src/hooks/useLanguageChange';
import {
    clearKeyedTimeout,
    setKeyedTimeout,
    clearAllKeyedTimeouts,
    buildBody,
} from './editUserHelpers';

const ROLE4_VOU_IDS = new Set(['4', '94', '1', '11']);

/**
 * @param {object} options
 * @param {string} options.mgmtUserRoute route to navigate back/after save
 * @param {string|number} [options.customerId] override for the customerId sent to
 *   updateUserAndRole. Defaults to userInfo?.customerId when omitted.
 * @param {boolean} [options.includeContactFieldsInNonRole3Payload] whether the
 *   non role-3 save also sends email/name/surname/mobilePhones.
 * @param {boolean} [options.navigateOnNonRole3Success] whether a successful
 *   non role-3 save navigates back to mgmtUserRoute.
 */
export default function useEditUserLogic({
    mgmtUserRoute,
    customerId,
    includeContactFieldsInNonRole3Payload = false,
    navigateOnNonRole3Success = false,
    refetchCheckListOnRoleChange = false,
}) {
    const history = useHistory();
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const {
        userDetail,
        userInfo,
        claims,
        addressList,
        tempFormList,
        claimList,
        newClaimList,
        newAddressList,
        deletedClaims,
        deletedAddress,
        selectedAddressType,
    } = useSelector((state) => state?.user);
    const { activeProductList } = useSelector((state) => state?.customerVouGroups);
    const { userTypeList } = useSelector((state) => state?.userTypes);

    const effectiveCustomerId = customerId ?? userInfo?.customerId;

    const [selectedRole, setSelectedRole] = useState(null);
    const [state, setState] = useState([]);
    const [currentFormValues, setCurrentFormValues] = useState(null);
    const [claimsSearchText, setClaimsSearchText] = useState({});
    const [addressText, setAddressText] = useState({});
    const [initialValues, setInitialValues] = useState(false);
    const [checkedProducts, setCheckedProducts] = useState([]);
    const [uncheckedProductIds, setUncheckedProductIds] = useState([]);
    const [runSetAllProductFields, setRunSetAllProductFields] = useState([]);
    const [checkVouId5000002ForUBY, setCheckVouId5000002ForUBY] = useState(false);
    const [tempClaimList5000002, setTempClaimList5000002] = useState(null);
    const [temp2ClaimList5000002, setTemp2ClaimList5000002] = useState(null);

    const hasInitializedCheckedProducts = useRef(false);
    const pendingRunProductTimeoutRef = useRef({});
    const pendingSetAllProductTimeoutRef = useRef({});
    const userInitiatedRoleChangeRef = useRef(false);
    const pendingRoleClaimsResetRef = useRef(false);

    const searchText = useText('writeTheFunctionYouAreLookingFor');
    const addressSearchText = useText('writeTheAddressYouAreLookingFor');
    const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

    const changeAddressPagination = (PageNumber, PageSize, vouId) =>
        getAddressList(PageNumber, PageSize, vouId, addressText[vouId]);

    const changeClaimsPagination = (PageNumber, PageSize, vouId) =>
        getClaimList(PageNumber, PageSize, vouId, claimsSearchText[vouId]);

    const hasProductEnoughClaims = (product) =>
        product?.vous?.some((vou) => {
            const selectedClaims = claimList[vou?.vouId] || newClaimList[vou?.vouId] || [];
            return selectedClaims.length > 1;
        });

    const isProductChecked = (product) =>
        checkedProducts?.some((item) => item.groupId === product?.groupId) ||
        (!uncheckedProductIds.includes(product?.groupId) && hasProductEnoughClaims(product));

    const tabChange = async () => null;

    const clearVouFieldsForProducts = (productsToClear, { resetAddress = true } = {}) => {
        const clearedClaimList = { ...claimList };
        const clearedNewClaimList = { ...newClaimList };
        const clearedProducts = { ...(form.getFieldValue('products')) };
        productsToClear.forEach((p) => {
            (p?.vous || []).forEach((vou) => {
                clearedClaimList[vou.vouId] = [];
                clearedNewClaimList[vou.vouId] = [];
                const prevVouObj = clearedProducts[vou.vouId] || {};
                clearedProducts[vou.vouId] = resetAddress
                    ? { ...prevVouObj, groupIds: [], addressList: [] }
                    : { ...prevVouObj, groupIds: [] };
            });
        });
        dispatch(setClaimList(clearedClaimList));
        dispatch(setNewClaimList(clearedNewClaimList));
        form.setFieldsValue({ products: clearedProducts });
    };

    const clearPendingProductTimeouts = () => {
        clearAllKeyedTimeouts(pendingRunProductTimeoutRef);
        clearAllKeyedTimeouts(pendingSetAllProductTimeoutRef);
        setRunSetAllProductFields([]);
    };

    const buildProductFormInitialValues = (products, skipCheckMerge = false) => {
        const formInitialValues = [];
        const fetchPromises = [];
        products?.forEach((product) => {
            product?.vous.forEach((vou) => {
                fetchPromises.push(
                    getClaimList(1, 10, vou?.vouId, undefined, skipCheckMerge),
                    getAddressList(1, 10, vou?.vouId),
                );
                formInitialValues[vou?.vouId] = {
                    vouId: vou.vouId,
                    groupId: product.groupId,
                    addressSelectionType: false,
                    groupIds: [],
                    addressList: [],
                };
            });
        });
        return { formInitialValues, fetchPromises };
    };

    const handleRoleProductsUpdate = (role, products) => {
        if (!products?.length) return;
        hasInitializedCheckedProducts.current = true;
        clearPendingProductTimeouts();
        if (role === 4) {
            const target = products.find((p) => p.groupId === 5000001);
            const otherIds = products.filter((p) => p.groupId !== 5000001).map((p) => p.groupId);
            setUncheckedProductIds(otherIds);
            setCheckedProducts(target ? [target] : []);
            clearVouFieldsForProducts(products.filter((p) => p.groupId !== 5000001));
            if (target) {
                target.vous.forEach((vou) => {
                    getClaimList(1, 10, vou?.vouId);
                });
                setCheckVouId5000002ForUBY(true);
                // 5000001 is auto-selected and its checkbox is disabled for ÜBY, so the
                // user never clicks it and the normal clickActiveProduct full-claims
                // fetch never runs for it. Fetch every claim (no pagination) right here
                // and write it straight into groupIds so it's guaranteed to be sent.
                (async () => {
                    const claimIdsByVou = {};
                    await Promise.all(
                        (target.vous || []).map(async (vou) => {
                            const body = buildBody(
                                { vouId: vou.vouId, userId: userDetail?.userId, userTypeId: role },
                            );
                            const action = await dispatch(
                                getUserCheckedFunctions({ body, pageNumber: 1, pageSize: 999999 }),
                            );
                            claimIdsByVou[vou.vouId] = getUserCheckedFunctions.fulfilled.match(action)
                                ? (action?.payload?.data?.items || []).map((c) => c.id)
                                : [];
                        }),
                    );
                    await setAllProductFields(target, claimIdsByVou);
                })();
            }
        } else if (role === 1) {
            setUncheckedProductIds([]);
            setCheckedProducts([...products]);
            products.forEach((product) => { product?.vous?.forEach((vou) => { getClaimList(1, 10, vou?.vouId); }); });
            const allVous = products.flatMap((p) => p?.vous || []);
            const syntheticProduct = { groupId: 'all-selected-products', vous: allVous };
            setCheckVouId5000002ForUBY(false);
            setKeyedTimeout(
                pendingRunProductTimeoutRef,
                syntheticProduct.groupId,
                () => setRunSetAllProductFields((prev) => [...(prev || []), syntheticProduct]),
                1500,
            );
        } else {
            setCheckedProducts([]);
            setUncheckedProductIds(products.map((p) => p.groupId));
            clearVouFieldsForProducts(products);
        }
    };

    const toggleListItem = (sourceMap, vouId, value, isChecked, isDeleteMode = false) => {
        const current = sourceMap[vouId] || [];
        if (isDeleteMode) {
            const next = isChecked ? current.filter((item) => item !== value) : [...current, value];
            return { ...sourceMap, [vouId]: next };
        }
        const next = isChecked ? Array.from(new Set([...current, value])) : current.filter((f) => f !== value);
        return { ...sourceMap, [vouId]: next };
    };

    const handleClean = () => {
        [setNewClaimList, setNewAddressList, setTempFormList,
            setClaimList, setDeletedAddress, setDeletedClaims, setAddressSelectionType,
        ].forEach((action) => dispatch(action({})));
    };

    useEffect(() => {
        handleClean();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history]);

    useEffect(async () => {
        handleClean();
        setClaimsSearchText({});
        setAddressText({});
        setCheckedProducts([]);
        setUncheckedProductIds([]);
        hasInitializedCheckedProducts.current = false;
        setState([]);
        // Not: getCheckList sadece userId'ye göre kullanıcının halihazırda kayıtlı
        // olan (eski rolüne ait) seçili claim/adreslerini döner; rol parametresi
        // almaz. refetchCheckListOnRoleChange olmayan sayfalarda burada tekrar
        // çağrılırsa, kullanıcı rolü manuel değiştirdiğinde eski rolün tikli
        // claim'leri yeni role sızar.
        if (refetchCheckListOnRoleChange) {
            dispatch(getCheckList(userDetail?.userId));
        }

        // Tüm ürünlerin claim ve adres checkbox'larını sıfırla
        const prevProducts = form.getFieldValue('products') || {};
        const clearedProducts = {};
        Object.keys(prevProducts).forEach((vouId) => {
            clearedProducts[vouId] = {
                ...prevProducts[vouId],
                groupIds: [],
                addressList: [],
                addressSelectionType: false,
            };
        });
        await form.setFieldsValue({ products: clearedProducts });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole]);

    useEffect(() => {
        try {
            if (
                activeProductList?.length > 0 &&
                Object.keys(newClaimList || {}).length > 0 &&
                !hasInitializedCheckedProducts.current
            ) {
                const productsWithSelectedClaims = activeProductList.filter(hasProductEnoughClaims);
                setCheckedProducts(productsWithSelectedClaims);
                hasInitializedCheckedProducts.current = true;
            }
        } catch (error) {
            console.error(error);
        }
    }, [activeProductList, newClaimList]);

    useEffect(() => {
        if (!userInitiatedRoleChangeRef.current) {
            return;
        }
        if (!activeProductList?.length) {
            return;
        }
        handleRoleProductsUpdate(selectedRole, activeProductList);
        userInitiatedRoleChangeRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole, activeProductList]);

    const showUpdateSuccess = (action, onOk) => {
        successDialog({
            title: <Text t="editSuccessful" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            ...(onOk ? { onOk } : {}),
        });
    };

    const onFinish = async () => {
        const values = await form.getFieldsValue();
        if (selectedRole === 3) {
            const userRoleInfo = [];
            state?.forEach((product) => {
                product?.vous?.forEach((vou) => {
                    if (claimList[vou.vouId]) {
                        userRoleInfo.push({
                            vouId: vou.vouId,
                            groupId: product.groupId,
                            groupIds: claimList[vou.vouId],
                            addressList: (selectedAddressType[vou.vouId] ?? addressList[vou.vouId]?.addressSelectionType ?? false)
                                ? tempFormList[vou.vouId]?.filter(
                                      (f) => !deletedAddress[vou.vouId]?.includes(f),
                                  ) || []
                                : [],
                            addressSelectionType: selectedAddressType[vou.vouId] ?? addressList[vou.vouId]?.addressSelectionType ?? false,
                        });
                    }
                });
            });
            const action = await dispatch(
                updateUserAndRole({
                    userRoleInfo,
                    customerId: effectiveCustomerId,
                    userId: userDetail?.userId,
                    groupId: selectedRole,
                    email: values?.email,
                    name: values?.name,
                    surname: values?.surName,
                    mobilePhones: values?.mobilePhones
                        ?.replaceAll(')', '')
                        ?.replaceAll('(', '')
                        ?.replaceAll('-', '')
                        ?.replaceAll(' ', ''),
                }),
            );
            if (updateUserAndRole.fulfilled.match(action)) {
                showUpdateSuccess(action, () => history.push({ pathname: mgmtUserRoute, state: 'edit' }));
            }
            return;
        }
        const productValues = await form.getFieldValue('products');
        const checkedVouIds = new Set(checkedProducts.flatMap((p) => (p?.vous || []).map((v) => String(v.vouId))),);
        const shouldIncludeAllProducts = selectedRole === 1;
        const filledGroupIds = productValues
            ? Object.entries(productValues)
                .filter(
                    ([vouId, value]) =>
                        (shouldIncludeAllProducts || checkedVouIds.has(String(vouId))) &&
                        value !== null &&
                        value !== undefined,
                )
                .map(([vouId, value]) => ({
                    vouId,
                    groupIds: value.groupIds,
                    addressList: value.addressList,
                    addressSelectionType: value.addressSelectionType,
                    groupId: value.groupId,
                }))
            : null;
        const formProduct = filledGroupIds?.length ? filledGroupIds : (initialValues || []);
        const filteredValues = formProduct
            ?.map((item) => {
                if (!item) return null;

                const groupIds = selectedRole === 1 ? [] : claimList[item?.vouId];

                const addressSelectionType =
                    selectedAddressType[item.vouId] ??
                    item.addressSelectionType ??
                    false;

                if (!addressSelectionType) {
                    return {
                        groupId: item.groupId,
                        vouId: item.vouId,
                        groupIds,
                        addressSelectionType: false,
                    };
                }

                return {
                    ...item,
                    groupIds,
                    addressSelectionType: true,
                    addressList:
                        tempFormList[item.vouId] ||
                        newAddressList[item.vouId] ||
                        item.addressList ||
                        [],
                };
            })
            .filter(Boolean);
        const flag = filteredValues?.some((item) => ROLE4_VOU_IDS.has(String(item.vouId)) && item.groupIds?.length > 0,);
        if (selectedRole === 4 && (!flag || !checkedProducts || checkedProducts.length === 0)) {
            warningDialog({
                title: <Text t="information" />,
                message: 'Devam edebilmek için lütfen en az bir adet ürün seçiniz.',
            });
            return;
        }
        const contactFields = includeContactFieldsInNonRole3Payload
            ? {
                email: values?.email,
                name: values?.name,
                surname: values?.surName,
                mobilePhones: values?.mobilePhones
                    ?.replaceAll(')', '')
                    ?.replaceAll('(', '')
                    ?.replaceAll('-', '')
                    ?.replaceAll(' ', ''),
            }
            : {};
        const action = await dispatch(
            updateUserAndRole({
                userRoleInfo: [...filteredValues],
                customerId: effectiveCustomerId,
                userId: userDetail?.userId,
                groupId: values?.groupId,
                ...contactFields,
            }),
        );
        if (updateUserAndRole.fulfilled.match(action)) {
            showUpdateSuccess(
                action,
                navigateOnNonRole3Success
                    ? () => history.push({ pathname: mgmtUserRoute, state: 'edit' })
                    : undefined,
            );
        }
    };

    const getClaimList = (PageNumber, PageSize, vouId, text, skipCheckMerge = false) => {
        const body = buildBody({ vouId, userId: userDetail?.userId, userTypeId: selectedRole }, text);
        return dispatch(
            getUserCheckedFunctions({
                body,
                pageNumber: PageNumber,
                pageSize: PageSize,
                ...(skipCheckMerge ? { skipCheckMerge } : {}),
            }),
        );
    };

    const getAddressList = useCallback(
        (PageNumber, PageSize, vouId, text) => {
            const body = buildBody({ vouId, userId: userDetail?.userId, customerId: userInfo?.customerId }, text);
            return dispatch(getListUserAddressLookup({ body, PageNumber, PageSize }));
        },
        [userDetail, userInfo, dispatch],
    );

    const syncProductFormValues = useCallback(
        (vouId, updatedFields) => {
            const formValues = form.getFieldsValue();
            const nextFormValues = {
                ...formValues,
                products: {
                    ...(formValues?.products),
                    [vouId]: { ...(formValues?.products?.[vouId]), ...updatedFields },
                },
            };
            form.setFieldsValue(nextFormValues);
            setCurrentFormValues(nextFormValues);
        },
        [form],
    );

    const handleToggleChange = (e, vouId, id, { deleteMap, deleteAction, mainMap, mainAction, newMap, newAction, formKey }) => {
        dispatch(deleteAction(toggleListItem(deleteMap, vouId, id, e, true)));
        const updated = toggleListItem(mainMap, vouId, id, e);
        dispatch(mainAction(updated));
        dispatch(newAction(toggleListItem(newMap, vouId, id, e)));
        syncProductFormValues(vouId, { [formKey]: updated[vouId] });
    };

    const handleClaimsChange = (e, vouId, claim) =>
        handleToggleChange(e, vouId, claim.id, {
            deleteMap: deletedClaims, deleteAction: setDeletedClaims,
            mainMap: claimList, mainAction: setClaimList,
            newMap: newClaimList, newAction: setNewClaimList,
            formKey: 'groupIds',
        });

    const handleAddressChange = (e, vouId, addressId) =>
        handleToggleChange(e, vouId, addressId, {
            deleteMap: deletedAddress, deleteAction: setDeletedAddress,
            mainMap: tempFormList, mainAction: setTempFormList,
            newMap: newAddressList, newAction: setNewAddressList,
            formKey: 'addressList',
        });

    const handleAddressSelectionTypeChange = (vouId, value) => {
        setCurrentFormValues(form.getFieldsValue());
        dispatch(setAddressSelectionType({ ...selectedAddressType, [vouId]: value }));
    };

    const handleRoleSelect = (value) => {
        userInitiatedRoleChangeRef.current = true;
        pendingRoleClaimsResetRef.current = true;
        setSelectedRole(value);
    };

    const cancel = () => {
        handleClean();
        history.push(mgmtUserRoute);
    };

    const getUserInformation = async () => {
        const action = await dispatch(getUserInfo({ userId: userDetail.userId }));
        if (getUserInfo.fulfilled.match(action)) {
            dispatch(getCheckList(userDetail?.userId));
        } else {
            errorDialog({
                title: <Text t="error" />,
                message: action?.payload?.error?.message,
                onOk: () => history.push({ pathname: mgmtUserRoute, state: 'edit' }),
            });
        }
    };

    useEffect(() => {
        getUserInformation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDetail]);

    useEffect(() => {
        setSelectedRole(userInfo?.groupId);
    }, [userInfo?.groupId]);

    // Role names (userTypeList) and the contact autocomplete come from the backend and are
    // otherwise only fetched once, so they'd stay in the previous language while this form is
    // open. Deliberately NOT re-running the product/claims/addresses block below here: that
    // rebuilds the whole form via form.setFieldsValue and would wipe any of the operator's
    // in-progress (unsaved) checkbox selections - a refetch is not worth that regression.
    useLanguageChange(() => {
        dispatch(getUserTypesList());
        dispatch(userContactLookup());
    });

    useEffect(() => {
        if (userInfo?.customerId && selectedRole) {
            dispatch(getUserTypesList());
            dispatch(userContactLookup());
            (async () => {
                const action = await dispatch(getActiveProducts());
                if (getActiveProducts.fulfilled.match(action)) {
                    const data = action?.payload?.data || [];
                    if (data?.length > 0) {
                        // Roles without a dedicated auto-select flow (i.e. anything other than
                        // 1/Yönetici or 4/ÜBY) must end up with a genuinely empty selection when
                        // the user manually switches into them. The claims refetch below would
                        // otherwise report the user's previously saved (old-role) checked claims
                        // via isCheck and re-tick them, so suppress that merge entirely up front
                        // instead of racing to undo it afterwards.
                        const shouldSkipCheckMerge =
                            pendingRoleClaimsResetRef.current && selectedRole !== 1 && selectedRole !== 4;
                        const { formInitialValues, fetchPromises } = buildProductFormInitialValues(
                            data,
                            shouldSkipCheckMerge,
                        );
                        form.setFieldsValue({
                            groupId: selectedRole,
                            customerId: userInfo?.customerId,
                            name: userInfo?.name,
                            surName: userInfo?.surName,
                            email: userInfo?.email,
                            mobilePhones: userInfo?.mobilePhones,
                            userId: userDetail.userId,
                            products: formInitialValues,
                        });
                        setInitialValues(formInitialValues);
                        setState(data);

                        // Extra safety net: re-assert the empty selection once every fetch has
                        // settled, in case anything else raced in a stray write in the meantime.
                        if (pendingRoleClaimsResetRef.current) {
                            if (selectedRole !== 1 && selectedRole !== 4) {
                                await Promise.all(fetchPromises);
                                clearVouFieldsForProducts(data);
                            }
                            pendingRoleClaimsResetRef.current = false;
                        }
                    }
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userDetail?.userId, userInfo, selectedRole]);

    const buildVouCheckedValues = (vou, product) => ({
        vouId: vou.vouId,
        groupId: product.groupId,
        addressSelectionType: selectedAddressType[vou.vouId] ?? addressList[vou.vouId]?.addressSelectionType,
        groupIds: claimList[vou.vouId]?.filter((f) => !deletedClaims[vou.vouId]?.includes(f)),
        addressList: tempFormList[vou.vouId]?.filter((f) => !deletedAddress[vou.vouId]?.includes(f)),
    });

    useEffect(async () => {
        const formValues = await form.getFieldsValue();
        const formCheckedValues = [];
        state?.forEach((product) => {
            product?.vous.forEach((vou) => {
                formCheckedValues[vou.vouId] = buildVouCheckedValues(vou, product);
            });
        });
        form.setFieldsValue({ ...formValues, products: formCheckedValues });
        setInitialValues(formCheckedValues);
        setCurrentFormValues({ ...formValues, products: formCheckedValues });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, claims, addressList]);

    useEffect(() => {
        (runSetAllProductFields || []).forEach((product) => {
            if (!pendingSetAllProductTimeoutRef.current[product.groupId]) {
                setKeyedTimeout(pendingSetAllProductTimeoutRef, product.groupId, () => setAllProductFields(product), 1000);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runSetAllProductFields]);

    const setAllProductFields = async (product, claimIdsByVou = null) => {
        try {
            const currentProducts = form.getFieldValue('products') || {};
            const prevProducts = currentProducts;
            const tempClaimList = { ...claimList };
            const tempNewClaimList = { ...newClaimList };
            const nextProducts = { ...currentProducts };
            (product?.vous || []).forEach((vou) => {
                const vouId = vou.vouId;
                const baseIds = claimIdsByVou
                    ? (claimIdsByVou[vouId] || [])
                    : (selectedRole === 3 || selectedRole === 4)
                        ? (claims[vouId]?.data || []).map((c) => c.id)
                        : (claimList[vouId] || []);
                const mergeIds = (a, b) => Array.from(new Set([...(a[vouId] || []), ...baseIds, ...(b[vouId] || [])]));
                const mergedClaimList = mergeIds(tempClaimList, claimList);
                const mergedNewClaimList = mergeIds(tempNewClaimList, newClaimList);
                const prevVouObj = prevProducts[vouId] || {};
                if (selectedRole !== 4 || !checkVouId5000002ForUBY || vou.vouId !== 5000002) {
                    tempClaimList[vouId] = mergedClaimList;
                    tempNewClaimList[vouId] = mergedNewClaimList;
                    nextProducts[vouId] = { ...prevVouObj, groupIds: mergedNewClaimList };
                } else {
                    setTempClaimList5000002(mergedNewClaimList);
                    setTemp2ClaimList5000002(mergedClaimList);
                }
                const v5000001 = 5000001;
                const v5000002 = 5000002;
                if (
                    vouId === 1 &&
                    nextProducts[vouId]?.groupIds?.length > 0 &&
                    selectedRole === 4 &&
                    prevProducts[v5000001]?.groupIds?.length > 0 &&
                    (prevProducts[v5000002]?.groupIds?.length ?? 0) <= 0
                ) {
                    tempClaimList[v5000002] = temp2ClaimList5000002;
                    tempNewClaimList[v5000002] = tempClaimList5000002;
                    nextProducts[v5000002] = { ...prevVouObj, groupIds: tempClaimList5000002 };
                }
            });
            await Promise.all([
                dispatch(setClaimList(tempClaimList)),
                dispatch(setNewClaimList(tempNewClaimList)),
            ]);
            form.setFieldsValue({ products: nextProducts });
        } catch (error) {
            console.error(error);
        }
        setRunSetAllProductFields((prev) => (prev || []).filter((p) => p.groupId !== product.groupId));
        setCheckVouId5000002ForUBY(null);
    };

    const clickActiveProduct = async (product, ifUBY = false) => {
        if (!product?.groupId) {
            return;
        }
        if (!isProductChecked(product)) {
            setUncheckedProductIds((prevIds) => prevIds.filter((item) => item !== product.groupId));
            setCheckedProducts((prevProducts = []) => {
                if (prevProducts.some((item) => item.groupId === product.groupId)) {
                    return prevProducts;
                }
                return [...prevProducts, product];
            });
            setCheckVouId5000002ForUBY(ifUBY);
            if (selectedRole === 3 || selectedRole === 4) {
                // ÜBY (4) / role 3: fetch every claim of the product's vous right away and
                // write them directly into groupIds. No timers involved, so this can't
                // be cancelled or race with picking another product.
                const claimIdsByVou = {};
                await Promise.all(
                    (product.vous || []).map(async (vou) => {
                        const body = buildBody(
                            { vouId: vou.vouId, userId: userDetail?.userId, userTypeId: selectedRole },
                        );
                        const action = await dispatch(
                            getUserCheckedFunctions({ body, pageNumber: 1, pageSize: 999999 }),
                        );
                        claimIdsByVou[vou.vouId] = getUserCheckedFunctions.fulfilled.match(action)
                            ? (action?.payload?.data?.items || []).map((c) => c.id)
                            : [];
                    }),
                );
                await setAllProductFields(product, claimIdsByVou);
            } else {
                product.vous.forEach((vou) => {
                    getClaimList(1, 10, vou?.vouId);
                });
                setKeyedTimeout(
                    pendingRunProductTimeoutRef,
                    product.groupId,
                    () => setRunSetAllProductFields((prev) => [...(prev || []), product]),
                    800,
                );
            }
        } else {
            clearKeyedTimeout(pendingRunProductTimeoutRef, product.groupId);
            clearKeyedTimeout(pendingSetAllProductTimeoutRef, product.groupId);
            setRunSetAllProductFields((prev) => (prev || []).filter((p) => p.groupId !== product.groupId));
            setUncheckedProductIds((prevIds) =>
                prevIds.includes(product.groupId) ? prevIds : [...prevIds, product.groupId],
            );
            setCheckedProducts((prevProducts = []) =>
                prevProducts.filter((item) => item.groupId !== product.groupId),
            );
            try {
                clearVouFieldsForProducts([product], { resetAddress: false });
            } catch (error) {
                console.error(error);
            }
        }
    };

    return {
        form,
        activeProductList,
        userInfo,
        userTypeList,
        selectedRole,
        handleRoleSelect,
        currentFormValues,
        addressText,
        setAddressText,
        addressSearchText,
        claimsSearchText,
        setClaimsSearchText,
        searchText,
        addressList,
        claims,
        isProductChecked,
        clickActiveProduct,
        getAddressList,
        getClaimList,
        handleAddressChange,
        handleClaimsChange,
        changeAddressPagination,
        changeClaimsPagination,
        handleAddressSelectionTypeChange,
        tabChange,
        cancel,
        onFinish,
    };
}
